(() => {
  if (customElements.get('card-product')) {
    return;
  }

  class CardProduct extends HTMLElement {
    constructor() {
      super();
      this.variantsImages = null;
      this.variants = null;
      this.imageElement = null;
      this.mediaLink = null;
      this.colorSwatchInputs = [];
      this.quickAddSizeInputs = [];
      this.quickAddButton = null;
      this.quickAddPanel = null;
      this.quickAddInFlight = false;
      this.cartDrawer = document.querySelector('cart-drawer');
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
    }

    connectedCallback() {
      this.init();
      document.addEventListener('click', this.handleDocumentClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.handleDocumentClick);
    }

    init() {
      const variantsImagesScript = this.querySelector('[data-variants-images]');
      if (!variantsImagesScript) return;

      try {
        this.variantsImages = JSON.parse(variantsImagesScript.textContent);
      } catch (error) {
        console.error('Error parsing variants images data:', error);
        return;
      }

      const cardVariantsScript = this.querySelector('[data-card-variants]');
      if (cardVariantsScript) {
        try {
          this.variants = JSON.parse(cardVariantsScript.textContent);
        } catch (error) {
          console.error('Error parsing card variants data:', error);
        }
      }

      const mediaContainer = this.querySelector('.media');
      if (!mediaContainer) return;

      this.mediaLink = mediaContainer;
      if (!this.mediaLink) return;

      this.imageElement = mediaContainer.querySelector('img');
      if (!this.imageElement) return;

      const colorSwatchesContainer = this.querySelector('.card-product__swatches');
      this.colorOptionPosition = colorSwatchesContainer
        ? parseInt(colorSwatchesContainer.dataset.colorOptionPosition, 10)
        : null;

      this.colorSwatchInputs = this.querySelectorAll(
        '.card-product__swatches input[type="radio"], .card-product__swatches input[type="checkbox"]'
      );

      this.colorSwatchInputs.forEach((input) => {
        input.addEventListener('change', (event) => {
          this.handleSwatchChange(event);
        });
      });

      this.quickAddButton = this.querySelector('[data-quick-add-toggle]');
      this.quickAddPanel = this.querySelector('[data-quick-add-panel]');
      this.quickAddCloseButton = this.querySelector('[data-quick-add-close]');
      this.errorWrapper = this.querySelector('[data-card-error-wrapper]');
      this.sizeOptionPosition = this.quickAddPanel
        ? parseInt(this.quickAddPanel.dataset.sizeOptionPosition, 10)
        : null;
      this.quickAddSizeInputs = this.querySelectorAll('[data-quick-add-size-value]');

      this.quickAddSizeInputs.forEach((input) => {
        input.addEventListener('change', () => {
          if (input.classList.contains('is-unavailable')) return;
          this.addVariantToCart(this.getVariantIdForSize(input.value));
        });
      });

      this.updateQuickAddSizesAvailability();

      if (this.quickAddButton) {
        this.quickAddButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.handleQuickAddToggle();
        });
      }

      if (this.quickAddCloseButton) {
        this.quickAddCloseButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.closeQuickAddPanel();
        });
      }
    }

    handleSwatchChange(event) {
      if (!this.variantsImages) return;

      this.resetQuickAddSizeSelection();

      let variantImageData = null;
      const variantId = event.target.dataset.variantId;

      if (variantId) {
        variantImageData = this.variantsImages.find(
          (variant) => variant.id === parseInt(variantId, 10)
        );
      }

      if (!variantImageData && this.colorOptionPosition) {
        const optionValue = event.target.value;
        const optionKey = `option${this.colorOptionPosition}`;

        variantImageData = this.variantsImages.find((variant) => {
          return (
            variant[optionKey] === optionValue &&
            variant.image &&
            variant.image.src
          );
        });

        if (!variantImageData) {
          variantImageData = this.variantsImages.find(
            (variant) => variant[optionKey] === optionValue
          );
        }
      }

      if (!variantImageData || !variantImageData.image) return;

      this.updateImage(variantImageData.image);

      if (variantImageData.url && this.mediaLink) {
        this.mediaLink.setAttribute('href', variantImageData.url);
      }

      this.updateQuickAddSizesAvailability();
    }

    updateImage(imageData) {
      if (!this.imageElement || !imageData) return;

      const isLazyLoaded = this.imageElement.classList.contains('lazyload');

      if (isLazyLoaded) {
        this.imageElement.setAttribute('data-src', imageData.src);
        if (imageData.srcset) {
          this.imageElement.setAttribute('data-srcset', imageData.srcset);
        }

        if (window.lazySizes) {
          lazySizes.loader.unveil(this.imageElement);
        }
      } else {
        this.imageElement.setAttribute('src', imageData.src);
        if (imageData.srcset) {
          this.imageElement.setAttribute('srcset', imageData.srcset);
        }
      }
    }

    handleQuickAddToggle() {
      if (!this.quickAddButton) return;
      this.handleErrorMessage();

      if (!this.quickAddPanel || this.quickAddSizeInputs.length === 0) {
        this.closeAllQuickAddPanels();
        const variantId = this.getQuickAddVariantId();
        this.addVariantToCart(variantId);
        return;
      }

      const isCurrentlyOpen = this.quickAddPanel.classList.contains('is-open');
      this.closeAllQuickAddPanels();
      const isOpen = !isCurrentlyOpen;
      this.quickAddPanel.classList.toggle('is-open', isOpen);
      this.quickAddButton.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    }

    closeQuickAddPanel() {
      if (!this.quickAddPanel) return;
      this.quickAddPanel.classList.remove('is-open');
      this.resetQuickAddSizeSelection();
      if (this.quickAddButton) {
        this.quickAddButton.setAttribute('aria-expanded', 'false');
      }
    }

    closeAllQuickAddPanels() {
      document.querySelectorAll('card-product').forEach((cardProduct) => {
        if (typeof cardProduct.closeQuickAddPanel === 'function') {
          cardProduct.closeQuickAddPanel();
        }
      });
    }

    resetQuickAddSizeSelection() {
      if (!this.quickAddSizeInputs || this.quickAddSizeInputs.length === 0) {
        return;
      }

      this.quickAddSizeInputs.forEach((input) => {
        input.checked = false;
      });
    }

    handleErrorMessage(errorMessage = false) {
      if (!this.errorWrapper) return;

      this.errorWrapper.classList.toggle('hidden', !errorMessage);
      this.errorWrapper.textContent = errorMessage || '';
    }

    getSelectedColor() {
      const selectedColorInput = this.querySelector('.card-product__swatches input:checked');
      return selectedColorInput ? selectedColorInput.value : null;
    }

    getQuickAddVariantId() {
      if (!this.variants || this.variants.length === 0) {
        return this.quickAddButton?.dataset.defaultVariantId || null;
      }

      const selectedColor = this.getSelectedColor();
      const colorOptionKey = this.colorOptionPosition ? `option${this.colorOptionPosition}` : null;

      if (selectedColor && colorOptionKey) {
        const colorMatch = this.variants.find((variant) => (
          variant[colorOptionKey] === selectedColor
          && variant.available
        ));

        if (colorMatch) {
          return colorMatch.id;
        }
      }

      const firstAvailable = this.variants.find((variant) => variant.available);
      return firstAvailable?.id || this.quickAddButton?.dataset.defaultVariantId || null;
    }

    getVariantIdForSize(sizeValue) {
      if (!this.variants || !this.sizeOptionPosition || !sizeValue) {
        return this.getQuickAddVariantId();
      }

      const sizeOptionKey = `option${this.sizeOptionPosition}`;
      const colorOptionKey = this.colorOptionPosition ? `option${this.colorOptionPosition}` : null;
      const selectedColor = this.getSelectedColor();

      let variantMatch = null;

      if (selectedColor && colorOptionKey) {
        variantMatch = this.variants.find((variant) => (
          variant[sizeOptionKey] === sizeValue
          && variant[colorOptionKey] === selectedColor
          && variant.available
        ));
        return variantMatch?.id || null;
      }

      variantMatch = this.variants.find((variant) => (
        variant[sizeOptionKey] === sizeValue
        && variant.available
      ));

      return variantMatch?.id || this.getQuickAddVariantId();
    }

    updateQuickAddSizesAvailability() {
      if (!this.variants || !this.sizeOptionPosition || this.quickAddSizeInputs.length === 0) {
        return;
      }

      const sizeOptionKey = `option${this.sizeOptionPosition}`;
      const colorOptionKey = this.colorOptionPosition ? `option${this.colorOptionPosition}` : null;
      const selectedColor = this.getSelectedColor();

      this.quickAddSizeInputs.forEach((input) => {
        const hasMatchingVariant = this.variants.some((variant) => {
          if (!variant.available) return false;
          if (variant[sizeOptionKey] !== input.value) return false;
          if (selectedColor && colorOptionKey) {
            return variant[colorOptionKey] === selectedColor;
          }

          return true;
        });

        input.classList.toggle('is-unavailable', !hasMatchingVariant);
      });
    }

    handleDocumentClick(event) {
      if (!this.quickAddPanel || !this.quickAddPanel.classList.contains('is-open')) {
        return;
      }

      if (this.contains(event.target)) {
        return;
      }

      this.closeQuickAddPanel();
    }

    addVariantToCart(variantId) {
      if (!variantId || this.quickAddInFlight) {
        return;
      }

      if (!this.cartDrawer) {
        return;
      }

      this.quickAddInFlight = true;
      this.handleErrorMessage();
      this.quickAddButton?.classList.add('is-loading');
      this.cartDrawer.setOpenAfterNextRender();

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      formData.append(
        'sections',
        this.cartDrawer.getSectionsToRender().map((section) => section.section),
      );
      formData.append('sections_url', window.location.pathname);

      Cart.add(formData)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.handleErrorMessage(response.description || window.cartStrings?.error || 'Quick add failed');
            return;
          }

          this.cartDrawer.renderContents(response);
          this.closeQuickAddPanel();
          this.handleErrorMessage();
        })
        .catch((error) => {
          console.error(error);
          this.handleErrorMessage(window.cartStrings?.error || error?.message || 'Quick add failed');
        })
        .finally(() => {
          this.quickAddInFlight = false;
          this.quickAddButton?.classList.remove('is-loading');
        });
    }
  }

  customElements.define('card-product', CardProduct);
})();

